import React, { useCallback, useState } from "react";
import { Button } from "../Button/Button";
import { Accordion } from "../Accordion/Accordion";
import { Checkbox } from "../Checkbox/Checkbox";
import "./FilterModal.scss";

/**
 * Modal para filtrado de contenido en desktop
 *
 * @param {boolean} isOpen - Controla si el modal está visible
 * @param {function} onClose - Función que se ejecuta al cerrar el modal
 * @param {object} savedFilters - Filtros guardados desde el estado global
 * @param {object} filterData - Datos de filtros desde endpoints
 * @param {object} tempFilters - Filtros temporales activos
 * @param {function} onTempFilterChange - Handler para cambios temporales
 * @param {function} onApplyFilters - Handler para aplicar filtros finales
 * @param {function} onClearFilters - Handler para limpiar filtros
 * @param {string} className - Clases CSS adicionales para el contenedor
 * @param {...*} props - Props adicionales que se pasan al elemento contenedor
 */
export const FilterModal = ({
  isOpen = false,
  onClose,
  savedFilters = {},
  filterData = {},
  tempFilters = {},
  onTempFilterChange,
  onApplyFilters,
  onClearFilters,
  className = "",
  ...props
}) => {
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const getOptionId = (option, categoryId) => {
    switch (categoryId) {
      case "tipos":
        return option.idTipoAprendizaje;
      case "temas":
        return option.idCategoria;
      case "idiomas":
        return option.idIdioma;
      case "dificultad":
        return option.idDificultad;
      default:
        return option.id;
    }
  };

  const isOptionSelected = useCallback(
    (optionId, categoryId) => {
      return tempFilters[categoryId]?.includes(optionId) || false;
    },
    [tempFilters]
  );

  const handleCheckboxChange = useCallback(
    (categoryId, optionId, checked) => {
      const isSingleSelection = categoryId === "tipos" && checked;
      onTempFilterChange?.(categoryId, optionId, checked, isSingleSelection);
    },
    [onTempFilterChange]
  );

  const handleAccordionChange = (accordionId) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? accordionId : null);
  };

  const renderCategoryOptions = (categoryId, data = []) => {
    return data.map((option) => {
      const optionId = getOptionId(option, categoryId);
      const isSelected = isOptionSelected(optionId, categoryId);

      return (
        <div key={optionId} className="filter-modal__options-checkbox">
          <Checkbox
            id={`${categoryId}-${optionId}`}
            checked={isSelected}
            onChange={(e) =>
              handleCheckboxChange(categoryId, optionId, e.target.checked)
            }
            className="paragraph-l-regular"
          >
            {option.nombre}
          </Checkbox>
        </div>
      );
    });
  };

  if (!isOpen) return null;

  return (
    <div className={`filter-modal ${className}`} {...props}>
      <div className="filter-modal__header">Filtrar</div>

      <div className="filter-modal__separator"></div>

      <div className="filter-modal__body">
        <Accordion
          title="Tipo"
          expanded={expandedAccordion === "tipos"}
          onChange={handleAccordionChange("tipos")}
        >
          <div className="filter-modal__options">
            {renderCategoryOptions("tipos", filterData.tipos)}
          </div>
        </Accordion>

        <Accordion
          title="Temas"
          expanded={expandedAccordion === "temas"}
          onChange={handleAccordionChange("temas")}
        >
          <div className="filter-modal__options">
            {filterData.temas &&
              filterData.temas
                .filter(
                  (item) =>
                    Array.isArray(item.subcategorias) &&
                    item.subcategorias.length > 0
                )
                .map((categoria) => (
                  <Accordion
                    key={categoria.idCategoria}
                    title={categoria.nombre}
                    titleTooltip={categoria.nombre}
                    titleClassName="paragraph-l-regular"
                    className="filter-modal__options-categoria"
                  >
                    {categoria.subcategorias
                      .filter((item) => Boolean(item.estado))
                      .map((subcategoria) => (
                        <div
                          key={subcategoria.idSubcategoria}
                          className="filter-modal__options-checkbox"
                          title={subcategoria.subcategoria}
                        >
                          <Checkbox
                            id={`temas-${subcategoria.idSubcategoria}`}
                            checked={isOptionSelected(
                              subcategoria.idSubcategoria,
                              "temas"
                            )}
                            onChange={(e) =>
                              handleCheckboxChange(
                                "temas",
                                subcategoria.idSubcategoria,
                                e.target.checked
                              )
                            }
                            className="paragraph-l-regular"
                          >
                            {subcategoria.subcategoria}
                          </Checkbox>
                        </div>
                      ))}
                  </Accordion>
                ))}
          </div>
        </Accordion>

        <Accordion
          title="Idioma"
          expanded={expandedAccordion === "idiomas"}
          onChange={handleAccordionChange("idiomas")}
        >
          <div className="filter-modal__options">
            {renderCategoryOptions("idiomas", filterData.idiomas)}
          </div>
        </Accordion>

        <Accordion
          title="Dificultad"
          expanded={expandedAccordion === "dificultad"}
          onChange={handleAccordionChange("dificultad")}
        >
          <div className="filter-modal__options">
            {renderCategoryOptions("dificultad", filterData.dificultad)}
          </div>
        </Accordion>
      </div>

      <div className="filter-modal__separator"></div>

      <div className="filter-modal__footer">
        <Button variant="secundario" size="small" onClick={onClearFilters}>
          LIMPIAR
        </Button>
        <Button variant="secundario" size="small" onClick={onApplyFilters}>
          APLICAR
        </Button>
      </div>
    </div>
  );
};
